/**
 * balanceData.ts — Balance Dashboard data layer (BETA 21.64).
 *
 * Normalizes every game-data object (items, perks, shrines, weapons, enemies) into a
 * flat list of EDITABLE FIELDS, each with a get/set that closes over the live object.
 * The dashboard renders these; edits call set() (mutating the live object) + persist to
 * Supabase via balanceOverrides. initBalanceData() snapshots code defaults then applies
 * saved overrides on top.
 *
 * Fields can be plain numbers, the rarity dropdown (a string), or the synthetic
 * "Spawn weight" number (how often this thing is offered — stored on the object as
 * __spawnWeight and read by the offer rollers via spawnWeightOf()).
 */

import { BUNCHA_BULLSHIIII } from './bunchaBullshiiii';
import { describeItem, regenItemDescription, TUNING_META } from './itemDescribe';
import { PEANUT_PERCS } from './peanutPercs';
import { SHRINE_BUFF_POOL } from './itemsAdapter';
import { WEAPON_DATABASE } from './weapons';
import { ENEMY_DATABASE } from './enemies';
import { WEAPON_MODS, MOD_ORDER } from './weaponMods';
import { getOverrides, spawnWeightOf } from './balanceOverrides';
export { spawnWeightOf };

export type FieldKind = 'number' | 'rarity' | 'weight';
export interface EditField {
  key: string;
  label: string;
  unit: string;              // '%', 'flat', 'sec', '×', '(0-1)', 'px/s', '/min', '°', ''
  kind: FieldKind;
  get: () => number | string;
  set: (v: number | string) => void;
  options?: string[];        // for the rarity dropdown
}

// Plain-English hover explanations, keyed by field key. mod:/stat fields fall back to a
// generic line. Shown as a tooltip (title) on each field label in the dashboard.
const FIELD_HELP: Record<string, string> = {
  rarity: 'Common / Rare / Legendary — sets the card color and how the rarity rollers treat it.',
  spawnWeight: 'OFFER RATE — how often this shows up as a choice. 1 = normal, 2 = twice as often, 0.5 = half. NOT a count/limit.',
  maxStacks: 'How many copies you can hold. Each copy adds its effect again.',
  bonusPerLevel: 'How much the stat goes up each time you pick this perk (per level).',
  maxLevel: 'Highest level this perk can reach (9999 = basically unlimited).',
  value: 'The buff amount this shrine grants.',
  baseDamage: 'Damage per hit at level 1.',
  damagePerLevel: 'Extra damage added each time the weapon levels up.',
  baseProjectileCount: 'How many projectiles fire per shot.',
  baseReloadTime: 'SECONDS BETWEEN SHOTS — this is the weapon\'s attack speed. LOWER = faster.',
  baseKnockback: 'How hard each hit shoves the enemy back.',
  baseCritChance: 'Chance to land a crit, on a 0–1 scale. 0.2 = 20% chance.',
  baseCritDamage: 'Bonus damage when you crit, as a multiplier. 1 = +100% (a crit does double).',
  spreadAngle: 'Cone width in degrees for multi-projectile weapons. 0 = dead straight.',
  'proj.speed': 'How fast the projectile travels (pixels per second).',
  'proj.size': 'Projectile radius in pixels — bigger is easier to land.',
  'proj.piercing': 'How many enemies one projectile passes through. 0 = stops on the first.',
  'proj.bounces': 'How many times the projectile bounces to another enemy or off a wall.',
  'proj.lifetime': 'Seconds before the projectile disappears.',
  baseHP: 'Enemy health at the very start of a run (before time scaling).',
  baseSpeed: 'Enemy move speed in pixels per second.',
  contactCooldown: 'Seconds between an enemy\'s touch-damage hits. Lower = hits you more often.',
  knockbackResist: '0 = full knockback, 1 = immune to knockback. (How much THIS enemy resists being pushed.)',
  contactKnockback: 'How hard this enemy SHOVES YOU on contact. Force = this + 3.5×the enemy\'s damage (the cap now scales with this value, so raising it actually flings you further). You\'re launched ≈ force÷4 pixels, then it decays over ~0.5s — e.g. 320 ≈ a nudge (~80px), 1500 ≈ ~370px, 4000 ≈ a big fling (~1000px). ⚠️ In a SWARM, shoves from opposite sides CANCEL out, so you barely move when fully surrounded — that\'s physics, not a bug. Your move speed also lets you walk back fast. Default 320.',
  'scaling.hpPerMinute': 'How fast enemy HP grows per minute. 0.7 = +70% each minute.',
  'scaling.damagePerMinute': 'Damage RAMP multiplier on the smart curve. 1 = the safe designed curve (this enemy ~2× base at 5 min, ~3.5× at 10 min — a fresh 100-HP player is NOT one-shot before ~7 min). 2 = twice as steep/deadly. Lower = gentler.',
  'scaling.speedPerMinute': 'How fast enemy speed grows per minute.',
  'throw.cooldown': 'Seconds between throws.',
  'throw.windup': 'Seconds the enemy winds up before a throw releases.',
  'throw.projectileSpeed': 'How fast the thrown projectile flies (px/s).',
  'throw.projectileDamageMultiplier': 'Throw damage as a fraction of the enemy\'s own damage.',
  'throw.minRange': 'Won\'t throw if you\'re closer than this.',
  'throw.maxRange': 'Won\'t throw if you\'re farther than this.',
};
// BETA 21.75 — repaint one item's card description after a dashboard edit. Also
// clears the chest-spin wrapped-text cache (registered by engine on a window hook)
// so the gold-spin card repaints with the new wording too.
export function regenItemById(id: string): void {
  const it = BUNCHA_BULLSHIIII.find(i => i.id === id);
  if (it) regenItemDescription(it);
  if (typeof window !== 'undefined') (window as unknown as { __clearChestCardCache?: () => void }).__clearChestCardCache?.();
}

export function helpForField(f: EditField): string {
  if (FIELD_HELP[f.key]) return FIELD_HELP[f.key];
  if (f.key.startsWith('mod:')) return `The buff amount per copy${f.unit === '%' ? ' (a percent)' : f.unit === 'flat' ? ' (a flat amount)' : ''}.`;
  if (f.key.startsWith('tune:')) return TUNING_META[f.key.slice(5)]?.help ?? `${f.label} — edit it and the in-game card text updates to match.`;
  return f.label;
}
// BETA 21.82 — Dylan: shrines have no sprites (blank boxes in the dashboard) — "maybe
// they could just be a logo/emoji depending on what they do (damage, crit, etc.)".
const STAT_EMOJI: Record<string, string> = {
  damageBonus: '💥', critChance: '🎯', critDamage: '💢', maxShield: '🛡️', maxShieldMult: '🛡️',
  shieldRegen: '🔋', armor: '🦺', hpRegen: '❤️', lifesteal: '🩸', luck: '🍀', moveSpeed: '👟',
  attackSpeedBonus: '⚡', pickupRange: '🧲', xpMultiplier: '⭐', goldMultiplier: '💰', evasion: '👻',
  knockbackBonus: '👊', thorns: '🌵', projectileCountBonus: '➕', projectileBounces: '🔄',
  dashRecharge: '💨', dashDuration: '💨',
};

export interface BalanceEntity {
  id: string;
  name: string;
  sprite: string;
  emoji?: string;            // fallback icon when there's no sprite (shrines)
  rarity: string;
  desc: string;              // human-readable "what it does"
  fields: EditField[];
}
export interface BalanceCategory {
  key: string;
  label: string;
  entities: BalanceEntity[];
}

const RARITY_OPTIONS = ['common', 'rare', 'legendary'];

function spawnWeightField(obj: object): EditField {
  return {
    key: 'spawnWeight', label: 'Offer rate', unit: 'x', kind: 'weight',
    get: () => (obj as { __spawnWeight?: number }).__spawnWeight ?? 1,
    set: v => { (obj as { __spawnWeight?: number }).__spawnWeight = Number(v); },
  };
}

function itemEntities(): BalanceEntity[] {
  return BUNCHA_BULLSHIIII.filter(it => it.status === 'active').map(it => {
    const fields: EditField[] = [
      { key: 'rarity', label: 'Rarity', unit: '', kind: 'rarity', options: RARITY_OPTIONS, get: () => it.rarity, set: v => { (it as { rarity: string }).rarity = String(v); } },
      spawnWeightField(it),
      { key: 'maxStacks', label: 'Max Stacks', unit: '', kind: 'number', get: () => it.maxStacks, set: v => { it.maxStacks = Number(v); } },
    ];
    it.statModifiers.forEach((m, i) => {
      fields.push({ key: `mod:${m.stat}:${i}`, label: m.stat, unit: m.isPercent ? '%' : 'flat', kind: 'number', get: () => m.value, set: v => { m.value = Number(v); } });
    });
    // BETA 21.75 — editable EFFECT magnitudes (burn dps, slow %, durations, proc
    // chance, heal %, etc.). Each tuning key becomes a number field; the handler
    // reads the live value via tuneOf, and editing it regenerates the card text.
    if (it.tuning) {
      for (const k of Object.keys(it.tuning)) {
        const meta = TUNING_META[k];
        fields.push({ key: `tune:${k}`, label: meta?.label ?? k, unit: meta?.unit ?? '', kind: 'number', get: () => it.tuning![k], set: v => { it.tuning![k] = Number(v); } });
      }
    }
    return { id: it.id, name: it.name, sprite: it.iconUrl, rarity: it.rarity, desc: describeItem(it) || (it.statModifiers.length === 0 ? '(effect handled in code)' : ''), fields };
  });
}

function percEntities(): BalanceEntity[] {
  return PEANUT_PERCS.filter(p => p.maxLevel > 1).map(p => ({
    id: p.id, name: p.name, sprite: p.iconUrl, rarity: 'perk', desc: p.description || '',
    fields: [
      spawnWeightField(p),
      { key: 'bonusPerLevel', label: `${p.stat} / level`, unit: (p.isPercent ? '%' : 'flat') as EditField['unit'], kind: 'number', get: () => p.bonusPerLevel, set: v => { p.bonusPerLevel = Number(v); } },
      { key: 'maxLevel', label: 'Max Level', unit: '' as EditField['unit'], kind: 'number', get: () => p.maxLevel, set: v => { p.maxLevel = Number(v); } },
    ],
  }));
}

function shrineEntities(): BalanceEntity[] {
  return SHRINE_BUFF_POOL.map(s => ({
    id: s.id, name: s.label, sprite: '', emoji: STAT_EMOJI[s.stat] ?? '✨', rarity: s.rarity, desc: `${s.label} (${s.stat})`,
    fields: [
      { key: 'rarity', label: 'Rarity', unit: '', kind: 'rarity', options: ['common', 'uncommon', 'rare', 'epic', 'legendary', 'negative'], get: () => s.rarity, set: v => { (s as { rarity: string }).rarity = String(v); } },
      spawnWeightField(s),
      { key: 'value', label: s.stat, unit: (s.pct ? '%' : 'flat') as EditField['unit'], kind: 'number', get: () => s.value, set: v => { s.value = Number(v); } },
    ],
  }));
}

function weaponEntities(): BalanceEntity[] {
  return WEAPON_DATABASE.map(w => {
    const f: EditField[] = [
      spawnWeightField(w),
      { key: 'baseDamage', label: 'Damage', unit: 'flat', kind: 'number', get: () => w.baseDamage, set: v => { w.baseDamage = Number(v); } },
      { key: 'damagePerLevel', label: 'Damage / level', unit: 'flat', kind: 'number', get: () => w.damagePerLevel, set: v => { w.damagePerLevel = Number(v); } },
      { key: 'baseProjectileCount', label: 'Projectiles', unit: 'flat', kind: 'number', get: () => w.baseProjectileCount, set: v => { w.baseProjectileCount = Number(v); } },
      { key: 'baseReloadTime', label: 'Attack speed (sec/shot)', unit: 'sec', kind: 'number', get: () => w.baseReloadTime, set: v => { w.baseReloadTime = Number(v); } },
      { key: 'baseKnockback', label: 'Knockback', unit: 'flat', kind: 'number', get: () => w.baseKnockback, set: v => { w.baseKnockback = Number(v); } },
      { key: 'baseCritChance', label: 'Crit chance', unit: '(0-1)', kind: 'number', get: () => w.baseCritChance, set: v => { w.baseCritChance = Number(v); } },
      { key: 'baseCritDamage', label: 'Crit damage', unit: '×', kind: 'number', get: () => w.baseCritDamage, set: v => { w.baseCritDamage = Number(v); } },
      { key: 'spreadAngle', label: 'Spread', unit: '°', kind: 'number', get: () => w.spreadAngle, set: v => { w.spreadAngle = Number(v); } },
    ];
    const pr = w.projectile;
    if (pr) {
      f.push(
        { key: 'proj.speed', label: 'Projectile speed', unit: 'px/s', kind: 'number', get: () => pr.speed, set: v => { pr.speed = Number(v); } },
        { key: 'proj.size', label: 'Projectile size', unit: 'flat', kind: 'number', get: () => pr.size, set: v => { pr.size = Number(v); } },
        { key: 'proj.piercing', label: 'Pierce', unit: 'flat', kind: 'number', get: () => pr.piercing, set: v => { pr.piercing = Number(v); } },
        { key: 'proj.bounces', label: 'Bounces', unit: 'flat', kind: 'number', get: () => pr.bounces, set: v => { pr.bounces = Number(v); } },
        { key: 'proj.lifetime', label: 'Lifetime', unit: 'flat', kind: 'number', get: () => pr.lifetime, set: v => { pr.lifetime = Number(v); } },
      );
    }
    return { id: w.id, name: w.name, sprite: w.spriteUrl, rarity: w.weaponType, desc: w.description || '', fields: f };
  });
}

function enemyEntities(): BalanceEntity[] {
  return Object.values(ENEMY_DATABASE).map(e => {
    const f: EditField[] = [
      { key: 'baseHP', label: 'HP', unit: 'flat', kind: 'number', get: () => e.baseHP, set: v => { e.baseHP = Number(v); } },
      { key: 'baseDamage', label: 'Touch damage', unit: 'flat', kind: 'number', get: () => e.baseDamage, set: v => { e.baseDamage = Number(v); } },
      { key: 'baseSpeed', label: 'Speed', unit: 'px/s', kind: 'number', get: () => e.baseSpeed, set: v => { e.baseSpeed = Number(v); } },
      { key: 'contactCooldown', label: 'Hit cooldown', unit: 'sec', kind: 'number', get: () => e.contactCooldown, set: v => { e.contactCooldown = Number(v); } },
      { key: 'knockbackResist', label: 'Knockback resist', unit: '(0-1)', kind: 'number', get: () => e.knockbackResist, set: v => { e.knockbackResist = Number(v); } },
      { key: 'contactKnockback', label: 'Knockback to player', unit: 'force', kind: 'number', get: () => e.contactKnockback, set: v => { e.contactKnockback = Number(v); } },
      { key: 'scaling.hpPerMinute', label: 'HP growth /min', unit: '/min', kind: 'number', get: () => e.scaling.hpPerMinute, set: v => { e.scaling.hpPerMinute = Number(v); } },
      { key: 'scaling.damagePerMinute', label: 'Damage ramp (×, 1=safe)', unit: '×', kind: 'number', get: () => e.scaling.damagePerMinute, set: v => { e.scaling.damagePerMinute = Number(v); } },
      { key: 'scaling.speedPerMinute', label: 'Speed growth /min', unit: '/min', kind: 'number', get: () => e.scaling.speedPerMinute, set: v => { e.scaling.speedPerMinute = Number(v); } },
    ];
    const ta = e.throwAttack;
    if (ta) {
      f.push(
        { key: 'throw.cooldown', label: 'Throw cooldown', unit: 'sec', kind: 'number', get: () => ta.cooldown, set: v => { ta.cooldown = Number(v); } },
        { key: 'throw.windup', label: 'Throw windup', unit: 'sec', kind: 'number', get: () => ta.windup, set: v => { ta.windup = Number(v); } },
        { key: 'throw.projectileSpeed', label: 'Throw speed', unit: 'px/s', kind: 'number', get: () => ta.projectileSpeed, set: v => { ta.projectileSpeed = Number(v); } },
        { key: 'throw.projectileDamageMultiplier', label: 'Throw dmg mult', unit: '×', kind: 'number', get: () => ta.projectileDamageMultiplier, set: v => { ta.projectileDamageMultiplier = Number(v); } },
        { key: 'throw.minRange', label: 'Throw min range', unit: 'flat', kind: 'number', get: () => ta.minRange, set: v => { ta.minRange = Number(v); } },
        { key: 'throw.maxRange', label: 'Throw max range', unit: 'flat', kind: 'number', get: () => ta.maxRange, set: v => { ta.maxRange = Number(v); } },
      );
    }
    return { id: e.id, name: e.name, sprite: e.eliteSprite ?? '', rarity: 'enemy', desc: `${e.name} — HP ${e.baseHP}, ${e.baseDamage} dmg`, fields: f };
  });
}

// Weapon mods (attachments) — one editable buff number per mod. Reads/writes the live
// WEAPON_MODS object; the generic override system persists + applies edits like everything else.
function modEntities(): BalanceEntity[] {
  return MOD_ORDER.map(code => {
    const m = WEAPON_MODS[code];
    const isPct = m.pct !== undefined;
    const field: EditField = {
      key: 'value', label: m.stat, unit: isPct ? '%' : 'flat', kind: 'number',
      get: () => (isPct ? m.pct! : m.flat!),
      set: v => { if (isPct) m.pct = Number(v); else m.flat = Number(v); },
    };
    return { id: m.id, name: `${m.name} (${code})`, sprite: m.iconUrl, rarity: 'common', desc: `${m.blurb} — ${m.label}`, fields: [field] };
  });
}

export function buildBalanceData(): BalanceCategory[] {
  return [
    { key: 'items', label: 'Items', entities: itemEntities() },
    { key: 'weapons', label: 'Weapons', entities: weaponEntities() },
    { key: 'mods', label: '🔧 Weapon Mods', entities: modEntities() },
    { key: 'enemies', label: 'Enemies', entities: enemyEntities() },
    { key: 'perks', label: 'Perks', entities: percEntities() },
    { key: 'shrines', label: 'Shrines', entities: shrineEntities() },
  ];
}

// ── Defaults snapshot + override apply ───────────────────────────────────────
let _defaults: Record<string, Record<string, Record<string, number | string>>> = {};
let _inited = false;

export function getDefault(cat: string, id: string, field: string): number | string | undefined {
  return _defaults[cat]?.[id]?.[field];
}
export function isBalanceDataInited(): boolean { return _inited; }

export function initBalanceData(): void {
  if (_inited) return;
  _inited = true;
  const data = buildBalanceData();
  _defaults = {};
  for (const cat of data) {
    _defaults[cat.key] = {};
    for (const ent of cat.entities) {
      _defaults[cat.key][ent.id] = {};
      for (const f of ent.fields) _defaults[cat.key][ent.id][f.key] = f.get();
    }
  }
  const ov = getOverrides();
  for (const cat of data) {
    for (const ent of cat.entities) {
      for (const f of ent.fields) {
        const v = ov[cat.key]?.[ent.id]?.[f.key];
        if (v !== undefined && v !== null) f.set(v);
      }
    }
  }
  // BETA 21.75 — after tuning/statModifier overrides apply, repaint every migrated
  // item's card description from its live numbers so picks reflect the edits on load.
  for (const it of BUNCHA_BULLSHIIII) regenItemDescription(it);
}
